import { pdf, Document, Image, Page, StyleSheet, Text, View } from '@react-pdf/renderer';
import { saveAs } from 'file-saver';
import logo from '../assets/images/logo.webp';
import logoFallback from '../assets/images/logo-placeholder.jpg';

const styles = StyleSheet.create({
	page: {
		padding: 24,
		fontSize: 10,
		fontFamily: 'Helvetica',
	},
	headerRow: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'center',
		marginBottom: 12,
	},
	logo: {
		width: 110,
		height: 34,
		objectFit: 'contain',
	},
	metaContainer: {
		alignItems: 'flex-end',
	},
	metaText: {
		fontSize: 9,
		marginBottom: 2,
	},
	title: {
		fontSize: 14,
		fontWeight: 700,
		marginBottom: 10,
	},
	table: {
		borderWidth: 1,
		borderColor: '#D9D9D9',
	},
	tableRow: {
		flexDirection: 'row',
		borderBottomWidth: 1,
		borderBottomColor: '#E8E8E8',
	},
	tableHeaderCell: {
		padding: 6,
		fontSize: 9,
		fontWeight: 700,
		backgroundColor: '#F7F7F7',
	},
	tableCell: {
		padding: 6,
		fontSize: 9,
	},
	footer: {
		marginTop: 10,
		fontSize: 8,
		color: '#666',
	},
});

const toDataUrl = (imageSrc, outputType = 'image/png') =>
	new Promise((resolve, reject) => {
		const image = new window.Image();
		image.onload = () => {
			const canvas = document.createElement('canvas');
			canvas.width = image.naturalWidth || image.width;
			canvas.height = image.naturalHeight || image.height;
			const context = canvas.getContext('2d');
			if (!context) {
				reject(new Error('Failed to create canvas context'));
				return;
			}
			context.drawImage(image, 0, 0);
			resolve(canvas.toDataURL(outputType));
		};
		image.onerror = () => reject(new Error('Failed to load logo image'));
		image.src = imageSrc;
	});

const resolvePdfLogoSource = async (dynamicLogoSrc) => {
	if (dynamicLogoSrc) {
		return dynamicLogoSrc;
	}

	try {
		return await toDataUrl(logo);
	} catch {
		return logoFallback;
	}
};

const PdfTableDocument = ({ logoSrc, title, investorDate, clientName, headers, rows }) => {
	const columnCount = headers.length || 1;
	const cellWidth = `${100 / columnCount}%`;

	return (
		<Document>
			<Page size="A4" style={styles.page}>
				<View style={styles.headerRow}>
					<Image src={logoSrc} style={styles.logo} />
					<View style={styles.metaContainer}>
						<Text style={styles.metaText}>Investor Date: {investorDate || '-'}</Text>
						<Text style={styles.metaText}>Client Name: {clientName || '-'}</Text>
					</View>
				</View>

				<Text style={styles.title}>{title}</Text>

				<View style={styles.table}>
					<View style={styles.tableRow}>
						{headers.map((header) => (
							<Text key={header} style={{ ...styles.tableHeaderCell, width: cellWidth }}>
								{header}
							</Text>
						))}
					</View>
					{rows.map((row, index) => (
						<View key={`row-${index}`} style={styles.tableRow}>
							{row.map((cell, cellIndex) => (
								<Text
									key={`cell-${index}-${cellIndex}`}
									style={{ ...styles.tableCell, width: cellWidth }}>
									{cell ?? '-'}
								</Text>
							))}
						</View>
					))}
				</View>

				<Text style={styles.footer}>Generated by Funding Souq</Text>
			</Page>
		</Document>
	);
};

export const downloadTablePdf = async ({
	title,
	investorDate,
	clientName,
	logoSrc: dynamicLogoSrc,
	headers = [],
	rows = [],
	fileName = 'document.pdf',
}) => {
	const logoSrc = await resolvePdfLogoSource(dynamicLogoSrc);
	const blob = await pdf(
		<PdfTableDocument
			logoSrc={logoSrc}
			title={title}
			investorDate={investorDate}
			clientName={clientName}
			headers={headers}
			rows={rows}
		/>
	).toBlob();

	saveAs(blob, fileName);
};
